/**
 * Display formatting. One implementation of each, imported everywhere.
 *
 * MONEY (doctrine 3): this module does not format money. It re-exports the server's
 * money() from src/money.ts so the console and the invoice and the API all render
 * "$1,204.50" from exactly one function. There is deliberately no console-local
 * dollars-and-cents code to drift, and no `toFixed(2)` anywhere in this file.
 *
 * TIME: every timestamp in this product is rendered twice — relative for the glance
 * ("4m ago") and absolute on hover ("Aug 5, 2026, 2:14:07 AM EDT"). An operator scanning
 * a night is reading the relative; an operator writing an incident note needs the
 * absolute, and having to open a second tab for it is how a console feels unfinished.
 */
import { money as renderMoney } from '../../../src/money.ts';
import type { AiMode, Disposition, Role, StandingFlag } from '../../../src/types.ts';

/** The single money renderer. Screens import `money` from here, never from money.ts. */
export const money = renderMoney;

// ------------------------------------------------------------------------ numbers

const NUM = new Intl.NumberFormat('en-US');

/** Thousands-separated integer. Pair with the `nd-num` class so columns line up. */
export function num(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return NUM.format(n);
}

/**
 * Percentages arrive from the API already computed (e.g. deflectedPct: 71). Rendering
 * them is a display concern; recomputing them in the console is how two screens end up
 * disagreeing about the same night.
 */
export function percent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

/** Ratio → percent, for the rare place the API hands over a numerator and denominator. */
export function ratioPercent(part: number, whole: number, digits = 0): string {
  if (!whole) return '—';
  return percent((part / whole) * 100, digits);
}

// ------------------------------------------------------------------------ duration

/**
 * Call length. mm:ss up to an hour, h:mm:ss past it. Always mono/tabular so a column of
 * durations is scannable — a 9:03 next to a 12:41 that do not align is a column nobody
 * reads.
 */
export function duration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) {
    return '—';
  }
  const s = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/** Long-form duration for prose: "1h 12m", "48m", "35s". */
export function durationWords(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) {
    return '—';
  }
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s}s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
}

/** Minutes, for the usage meter. */
export function minutes(mins: number | null | undefined): string {
  if (mins === null || mins === undefined || !Number.isFinite(mins)) return '—';
  return `${NUM.format(Math.round(mins))} min`;
}

// ------------------------------------------------------------------------ time

export function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "just now" / "4m ago" / "in 12m" / "3d ago". Deliberately coarse: the exact second is
 * one hover away, and a relative label that changes every render is a label that draws
 * the eye away from the thing that actually changed.
 */
export function relativeTime(iso: string | Date | null | undefined, now: Date = new Date()): string {
  const d = iso instanceof Date ? iso : parseIso(iso);
  if (!d) return '—';
  const delta = d.getTime() - now.getTime();
  const past = delta < 0;
  const abs = Math.abs(delta);

  let text: string;
  if (abs < 45_000) return past ? 'just now' : 'in a moment';
  if (abs < HOUR) text = `${Math.round(abs / MINUTE)}m`;
  else if (abs < DAY) {
    const h = Math.floor(abs / HOUR);
    const m = Math.round((abs % HOUR) / MINUTE);
    text = m && h < 6 ? `${h}h ${m}m` : `${h}h`;
  } else if (abs < 30 * DAY) text = `${Math.round(abs / DAY)}d`;
  else if (abs < 365 * DAY) text = `${Math.round(abs / (30 * DAY))}mo`;
  else text = `${Math.round(abs / (365 * DAY))}y`;

  return past ? `${text} ago` : `in ${text}`;
}

/** How often a <RelativeTime> should re-render itself, given how old it is. */
export function relativeTickMs(iso: string | Date | null | undefined, now: Date = new Date()): number {
  const d = iso instanceof Date ? iso : parseIso(iso);
  if (!d) return 60_000;
  const abs = Math.abs(d.getTime() - now.getTime());
  if (abs < HOUR) return 15_000;
  if (abs < DAY) return 60_000;
  return 10 * MINUTE;
}

function dtf(tz: string | undefined, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  // An unknown IANA zone throws inside Intl. A bad tenant timezone must degrade to the
  // browser's zone, not blank the screen that was going to tell you the network is down.
  try {
    return new Intl.DateTimeFormat('en-US', tz ? { ...opts, timeZone: tz } : opts);
  } catch {
    return new Intl.DateTimeFormat('en-US', opts);
  }
}

/** The hover title on every timestamp: full date, seconds, and the zone it is in. */
export function absoluteTime(iso: string | Date | null | undefined, tz?: string): string {
  const d = iso instanceof Date ? iso : parseIso(iso);
  if (!d) return 'unknown';
  return dtf(tz, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(d);
}

/** "2:14 AM" — the clock face in the status strip and the time column in tables. */
export function clockTime(iso: string | Date | null | undefined, tz?: string): string {
  const d = iso instanceof Date ? iso : parseIso(iso);
  if (!d) return '—';
  return dtf(tz, { hour: 'numeric', minute: '2-digit' }).format(d);
}

export function clockTimeSeconds(iso: string | Date | null | undefined, tz?: string): string {
  const d = iso instanceof Date ? iso : parseIso(iso);
  if (!d) return '—';
  return dtf(tz, { hour: 'numeric', minute: '2-digit', second: '2-digit' }).format(d);
}

/** "Aug 5" / "Aug 5, 2025" once the year differs from now. */
export function shortDate(iso: string | Date | null | undefined, tz?: string): string {
  const d = iso instanceof Date ? iso : parseIso(iso);
  if (!d) return '—';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return dtf(tz, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d);
}

/** "Tue, Aug 5 · 2:14 AM" — for a night's header row. */
export function dateAndTime(iso: string | Date | null | undefined, tz?: string): string {
  const d = iso instanceof Date ? iso : parseIso(iso);
  if (!d) return '—';
  return `${dtf(tz, { weekday: 'short', month: 'short', day: 'numeric' }).format(d)} · ${clockTime(d, tz)}`;
}

/** Short zone label ("EDT") for the strip, so a night shift knows whose clock this is. */
export function zoneLabel(tz: string | undefined, at: Date = new Date()): string {
  const parts = dtf(tz, { timeZoneName: 'short' }).formatToParts(at);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

// ------------------------------------------------------------------------ phone

/**
 * (304) 555-0147 from +13045550147.
 *
 * Anything that is not a NANP number is returned as-is rather than mangled: NightDesk
 * sells to US ISPs today, but a half-formatted international number in a callback field
 * is worse than an unformatted one — an operator will dial what is on the screen.
 */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return '—';
  const raw = e164.trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

/** The tel: href for a formatted number. Always the E.164, never the pretty form. */
export function telHref(e164: string | null | undefined): string | undefined {
  if (!e164) return undefined;
  const digits = e164.replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : undefined;
}

/** Last four, for "the number ending 0147" in confirmations. */
export function lastFour(value: string | null | undefined): string {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : digits;
}

// ------------------------------------------------------------------------ ids / text

/** First 8 of a uuid, for a table cell that needs an id but not the whole id. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 8 ? id.slice(0, 8) : id;
}

/** snake_case / kebab-case enum → "Sentence case". Used for statuses we do not map. */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  const words = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

export function truncate(value: string | null | undefined, max = 90): string {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

/** "1 call" / "4 calls" — English, at the one place that needs it. */
export function plural(count: number, one: string, many?: string): string {
  return `${NUM.format(count)} ${count === 1 ? one : (many ?? `${one}s`)}`;
}

// ------------------------------------------------------------------------ domain labels

/**
 * Domain enums render the same words on every screen. Keeping the map here rather than in
 * each screen is what stops the calls table saying "Ticket filed" while the report says
 * "ticket_filed" — the kind of seam that makes a product feel assembled rather than built.
 */
export const DISPOSITION_LABELS: Record<Disposition, string> = {
  informed: 'Informed',
  ticket_filed: 'Ticket filed',
  message_taken: 'Message taken',
  escalated: 'Escalated',
  abandoned: 'Abandoned',
  fallback_vm: 'Fell back to voicemail',
};

export const STANDING_LABELS: Record<StandingFlag, string> = {
  past_due: 'Past due',
  throttled: 'Throttled',
  suspended: 'Suspended',
  clear: 'Clear',
  unknown: 'Unknown',
};

export const AI_MODE_LABELS: Record<AiMode, string> = {
  full: 'Full AI',
  fallback: 'Fallback',
};

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  viewer: 'Viewer',
};

export function dispositionLabel(d: Disposition | null | undefined): string {
  return d ? DISPOSITION_LABELS[d] : 'In progress';
}

export function standingLabel(s: StandingFlag | null | undefined): string {
  return s ? STANDING_LABELS[s] : 'Unknown';
}

export function roleLabel(r: Role | null | undefined): string {
  return r ? ROLE_LABELS[r] : '—';
}
